import {
  DropdownFilters,
  defaultDropdownFilter
} from '@hooks/useAssessmentDropdownFilters'
import {
  GetAPIparams,
  getTitle,
  schoolLabels,
  teamLabels
} from '@utils/assessmentReportUtils'
import { Col, Flex, Row } from 'antd'
import { ReactNode } from 'react'

import { useLocation } from '@suntex.core/nav'
import { Entities_Login_StateInfo_Response } from '@suntex.fim/api-client'
import { isAuthenticated, useAuth } from '@suntex.fim/auth/react'
import {
  $SpecialGradeLevel,
  EntityLevel,
  SpecialGradeLevel
} from '@suntex.fim/enums'
import { useFramework, useRotateDevice } from '@suntex.fim/framework'
import { useOptionalPlayerContext } from '@suntex.fim/player'
import { useOptionalTeamInfo } from '@suntex.fim/team'
import { LevelSelect, YearSelect } from '@suntex.fim/ui'

import Report from './Report'

export function capitalizeFirstLetter(text: string | undefined): string {
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : ''
}

interface AssessmentReportNavigationProps {
  filters?: ReactNode
  exportButton?: ReactNode
  title?: string
  displayReport?: boolean
  dropdownFilters?: DropdownFilters
  setDropDownFilters?: (filters: DropdownFilters) => void
  isSchool?: boolean
  isPlayer?: boolean
}

const specialGradeOptions = $SpecialGradeLevel.listOptions([
  SpecialGradeLevel.AllGrades,
  SpecialGradeLevel.Grades3To8,
  SpecialGradeLevel.Kindergarten,
  SpecialGradeLevel.Grade1,
  SpecialGradeLevel.Grade2,
  SpecialGradeLevel.Grade3,
  SpecialGradeLevel.Grade4,
  SpecialGradeLevel.Grade5,
  SpecialGradeLevel.Grade6,
  SpecialGradeLevel.Grade7,
  SpecialGradeLevel.Grade8
])

/**
 * Renders the Assessment Report Navigation component.
 */
function AssessmentReportNavigation({
  filters,
  exportButton,
  title = 'Assessment Reports',
  displayReport = true,
  dropdownFilters = defaultDropdownFilter,
  setDropDownFilters,
  isSchool = false,
  isPlayer = false
}: AssessmentReportNavigationProps): JSX.Element {
  const { pathname } = useLocation()
  const { calendar, localization } = useFramework()
  const { login } = useAuth(isAuthenticated)
  const { level } = GetAPIparams(
    login.entityLevel === EntityLevel.School && pathname.includes('school'),
    defaultDropdownFilter,
    login.entityLevel === EntityLevel.Player && pathname.includes('player')
  )
  const playerTeam = useOptionalPlayerContext()?.playerInfo?.team
  const team = useOptionalTeamInfo()

  useRotateDevice()

  const getGoalIndexLLabel = (
    isTeamGoalIndex: boolean,
    label: string
  ): string => {
    if (!isTeamGoalIndex) return ''

    const isPlayer = login.entityLevel === EntityLevel.Player

    const labelMap: Record<string, string> = {
      Team: isPlayer
        ? playerTeam?.teamName || team?.name || ''
        : team?.name || '',
      'School Year': isPlayer
        ? playerTeam?.year || team?.year || ''
        : team?.year || '',
      Grade:
        (isPlayer ? (playerTeam?.grade ?? team?.grade) : team?.grade) != null
          ? String(isPlayer ? (playerTeam?.grade ?? team?.grade) : team?.grade)
          : ''
    }

    return labelMap[label] || 'NA'
  }
  const renderStats = () => {
    // Only show Team, School Year, Grade for AssessmentReportGoalIndex
    const isTeamGoalIndex =
      pathname.includes('goals-index') && pathname.includes('team')
    let labels = ['Region', 'District', 'School']
    if (isTeamGoalIndex) {
      labels = [...labels, 'Team', 'School Year', 'Grade']
    }
    const printOnlyTeamName =
      isTeamGoalIndex || pathname.includes('fluency-games')
        ? undefined
        : login.entityLevel === EntityLevel.Player
          ? playerTeam?.teamName || team?.name
          : team?.name

    return (
      <div className="main-navigation__stats">
        {labels.map((label) => {
          const key =
            `${label.toLowerCase()}Name` as keyof Entities_Login_StateInfo_Response
          let displayValue = ''
          if (label === 'Grade' && isTeamGoalIndex) {
            displayValue = getGoalIndexLLabel(isTeamGoalIndex, label)
          } else {
            displayValue =
              capitalizeFirstLetter(login[key] as string) ||
              getGoalIndexLLabel(isTeamGoalIndex, label)
          }

          return (
            <div key={label} className="stat">
              <span className="label">{localization.text(label)}</span>
              <span className="numbers">{displayValue}</span>
            </div>
          )
        })}
        {printOnlyTeamName && (
          <div className="stat print-only">
            <span className="label">Team</span>
            <span className="numbers">{printOnlyTeamName}</span>
          </div>
        )}
      </div>
    )
  }
  const getPostfix = () => {
    const reportType = pathname.split('/')[4]
    const labels = level === EntityLevel.School ? schoolLabels : teamLabels
    if (labels[reportType] && !isPlayer) {
      return <span className="print-only">{` - ${labels[reportType]}`}</span>
    }
    return null
  }

  const renderFilters = () =>
    isSchool && (
      <>
        <Col className="report-options">
          <p className="dd-label fw-bold text-sm text-chalkboard-txt">
            {localization.text('Grade')}
          </p>
          <LevelSelect
            value={dropdownFilters.grade}
            onChange={(grade) =>
              // eslint-disable-next-line no-restricted-syntax
              setDropDownFilters?.({ ...dropdownFilters, grade })
            }
            options={specialGradeOptions}
          />
        </Col>
        <Col className="report-options">
          <p className="dd-label fw-bold text-sm text-chalkboard-txt">
            School Year
          </p>
          <YearSelect
            value={dropdownFilters.year}
            onChange={(year: string) =>
              // eslint-disable-next-line no-restricted-syntax
              setDropDownFilters?.({ ...dropdownFilters, year })
            }
            options={[
              calendar.getPreviousSeason(),
              calendar.getCurrentSeason()
            ]}
          />
        </Col>
      </>
    )

  return (
    <>
      <Row gutter={[10, 10]} className="main-navigation" align="middle">
        <Col xs={{ flex: '55%' }} md={{ flex: 'auto' }}>
          <Flex gap="10px" className="main-navigation__heading" align="center">
            <h3>
              {getTitle(isSchool, isPlayer)} {title} {getPostfix()}
            </h3>
          </Flex>
        </Col>
        {login.entityLevel !== EntityLevel.Player && (
          <Col
            flex="auto"
            className={'main-navigation__btn-container print-hidden'}
          >
            {exportButton}
          </Col>
        )}
        <Col flex="100%">{renderStats()}</Col>
      </Row>
      {displayReport && (
        <Row gutter={[10, 12]} className="main-navigation report-filter">
          <Col>
            <Flex gap={6} flex="1 1 auto" vertical className="print-hidden">
              <p className="dd-label fw-bold text-sm">Report</p>
              <Report />
            </Flex>
          </Col>
        </Row>
      )}

      <Row gutter={[10, 12]} className="main-navigation report-filter">
        {renderFilters()}
        <Col xs={{ flex: '100%' }} sm={{ flex: '0 0 auto' }}>
          {filters}
        </Col>
      </Row>
    </>
  )
}

export default AssessmentReportNavigation
